import { DesktopOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons'
import { Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getThemePreference,
  setThemePreference,
  type ThemeMode,
} from './themePreference'

const ICONS: Record<ThemeMode, ReactNode> = {
  light: <SunOutlined />,
  dark: <MoonOutlined />,
  system: <DesktopOutlined />,
}

/**
 * Compact header control — light / dark / system.
 * Preference is stored in localStorage only (no backend).
 */
export default function ThemeToggle() {
  const { t } = useTranslation('common')
  const [mode, setMode] = useState<ThemeMode>(() => getThemePreference())

  useEffect(() => {
    setMode(getThemePreference())
  }, [])

  const onPick = (next: ThemeMode) => {
    setThemePreference(next)
    setMode(next)
  }

  const labelFor = (key: ThemeMode) => t(`theme.${key}`)

  const items: MenuProps['items'] = (['light', 'dark', 'system'] as ThemeMode[]).map((key) => ({
    key,
    icon: ICONS[key],
    label: labelFor(key),
    onClick: () => onPick(key),
  }))

  return (
    <Dropdown menu={{ items, selectedKeys: [mode] }} trigger={['click']} placement="bottomRight">
      <button type="button" className="tc-theme-toggle" aria-label={t('theme.ariaLabel')} title={labelFor(mode)}>
        {ICONS[mode]}
        <span className="tc-theme-toggle-label">{labelFor(mode)}</span>
      </button>
    </Dropdown>
  )
}
